// @flow

import React from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { SectionComponent, Button } from "@components";
import { AbsenceDetailsManagerNotesTable, AddManagerNotesModal } from "@modules/shared/components";
import type { State } from "@store/reducers";
import actionCreators from "@modules/shared/actionCreator";
import {
  getAddEditManagerNotesModal,
  getManagerNotesSchema,
} from "@modules/shared/selectors/employee-absences";
import { type PropertiesSchema } from "@types";
import type { ManagerNotesModal } from "@modules/shared/reducer/employee-absences/types";

type OwnProps = $Exact<{
  id: string,
  clientId: ?number,
  employeeId: number,
  absenceId: number,
}>;

type PropsFromState = $Exact<{
  addManagerNotesModal: ManagerNotesModal,
  managerNotesSchema: ?PropertiesSchema,
}>;

type PropsFromDispatch = $Exact<{
  getAbsenceManagerNotesSchema: typeof actionCreators.GetAbsenceManagerNotesSchema,
  toggleAddEditManagerNotesModal: typeof actionCreators.ToggleAddEditManagerNotesModal,
  addAbsenceManagerNote: typeof actionCreators.AddManagerNote,
  editAbsenceManagerNote: typeof actionCreators.EditManagerNote,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  isSectionOpen: boolean,
}>;

export class AbsenceDetailsManagerNotes extends React.Component<Props, ComponentState> {
  state = {
    isSectionOpen: true,
  };

  componentDidMount() {
    this.props.getAbsenceManagerNotesSchema();
  }

  showAddManagerNotesModal = () => {
    this.props.toggleAddEditManagerNotesModal({
      editMode: false,
      visible: true,
      data: null,
    });
  };

  closeAddManagerNotesModal = () => {
    this.props.toggleAddEditManagerNotesModal({
      editMode: false,
      visible: false,
      data: null,
    });
  };

  submitAddManagerNotesModal = (formData: Object) => {
    if (formData.id) {
      this.props.editAbsenceManagerNote({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        absenceId: this.props.absenceId,
        managerNoteId: formData.id,
        note: {
          id: formData.id,
          note_type: {
            id: formData.noteType.id,
          },
          notes: formData.notes,
        },
      });
    } else {
      this.props.addAbsenceManagerNote({
        clientId: this.props.clientId,
        absenceId: this.props.absenceId,
        employeeId: this.props.employeeId,
        note: {
          absenceId: this.props.absenceId,
          note_type: {
            id: formData.noteType.id,
          },
          notes: formData.notes,
        },
      });
    }
  };

  toggleSection = () => {
    this.setState({
      isSectionOpen: !this.state.isSectionOpen,
    });
  };

  render() {
    return (
      <SectionComponent
        id={this.props.id}
        title={<h5>{this.props.t("NOTES")}</h5>}
        wrapperClassName="flex-column"
        rightLink={
          <Button materialIcon="add_box" onClick={this.showAddManagerNotesModal}>
            {this.props.t("ADD")}
          </Button>
        }
        collapsable
        t={this.props.t}
        onTitleClick={this.toggleSection}
        isOpen={this.state.isSectionOpen}
      >
        <AbsenceDetailsManagerNotesTable
          clientId={this.props.clientId}
          employeeId={this.props.employeeId}
          absenceId={this.props.absenceId}
        />
        {this.props.addManagerNotesModal.visible && (
          <AddManagerNotesModal
            editMode={this.props.addManagerNotesModal.editMode}
            onDismissHandler={this.closeAddManagerNotesModal}
            schema={this.props.managerNotesSchema}
            onSubmit={this.submitAddManagerNotesModal}
            data={this.props.addManagerNotesModal.data}
          />
        )}
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  addManagerNotesModal: getAddEditManagerNotesModal(state),
  managerNotesSchema: getManagerNotesSchema(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getAbsenceManagerNotesSchema: actionCreators.GetAbsenceManagerNotesSchema,
  toggleAddEditManagerNotesModal: actionCreators.ToggleAddEditManagerNotesModal,
  addAbsenceManagerNote: actionCreators.AddManagerNote,
  editAbsenceManagerNote: actionCreators.EditManagerNote,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceDetailsManagerNotes);
